import type { SelectOption } from '@/components/SearchableSelect'
import type { WaterfallJeMonthsSummaryMonth, WaterfallJeStatusValue } from '@/types/waterfall'
import { incomeAllocationMonthsRange } from '@/utils/allocationFormat'

export type WaterfallJeStatusBadgeTone = 'gray' | 'amber' | 'emerald' | 'blue'

export function reportMonthLabel(reportMonth: string): string {
  const [yearStr, monthStr] = reportMonth.split('-')
  const year = Number(yearStr)
  const month = Number(monthStr)
  if (!Number.isFinite(year) || !Number.isFinite(month)) return reportMonth
  return new Date(year, month - 1, 1).toLocaleString('en-US', {
    month: 'short',
    year: 'numeric',
  })
}

export function monthFromIsoDate(isoDate: string): string {
  return isoDate.slice(0, 7)
}

export function uniqueMonthsDescending(months: string[]): string[] {
  return [...new Set(months)].sort((a, b) => b.localeCompare(a))
}

export function waterfallJeStatusLabel(
  status: WaterfallJeStatusValue,
  opts?: { content_dirty?: boolean; needs_qbo_update?: boolean },
): string {
  if (opts?.needs_qbo_update) return 'Needs QuickBooks update'
  if (status === 'stale' || opts?.content_dirty) return 'Out of date'
  switch (status) {
    case 'none':
      return 'No JE yet'
    case 'draft':
      return 'Draft'
    case 'posted':
      return 'Posted'
    default:
      return status
  }
}

export function waterfallJeStatusBadgeTone(
  status: WaterfallJeStatusValue,
  opts?: { content_dirty?: boolean; needs_qbo_update?: boolean },
): WaterfallJeStatusBadgeTone {
  if (opts?.needs_qbo_update || status === 'stale' || opts?.content_dirty) return 'amber'
  if (status === 'posted') return 'emerald'
  if (status === 'draft') return 'blue'
  return 'gray'
}

export function formatJePostedDate(value: string | null | undefined): string | null {
  if (!value) return null
  const dateOnly = value.slice(0, 10)
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOnly)
  if (!match) return value
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export type WaterfallMonthSelectStatus = 'open' | 'partial' | 'complete' | 'incomplete'

export function waterfallMonthSelectStatus(args: {
  jeStatus: WaterfallJeStatusValue
  checklistComplete: boolean | null
  checkedCount?: number | null
  incomeAllocation?: boolean
}): WaterfallMonthSelectStatus {
  if (args.checklistComplete === false) {
    return (args.checkedCount ?? 0) > 0 ? 'partial' : 'incomplete'
  }
  if (args.incomeAllocation) {
    // JE posted is itself a required checklist item, so a complete
    // checklist is the month-done signal — do not fall through to Open
    // when je_status is draft/stale/none.
    return args.checklistComplete === true ? 'complete' : 'open'
  }
  if (args.jeStatus === 'posted') return 'complete'
  if (args.jeStatus === 'draft' || args.jeStatus === 'stale') return 'partial'
  return 'open'
}

export function buildWaterfallMonthSelectOptions(
  months: WaterfallJeMonthsSummaryMonth[],
): SelectOption[] {
  return months.map((entry) => ({
    id: entry.report_month,
    label: reportMonthLabel(entry.report_month),
    status: waterfallMonthSelectStatus({
      jeStatus: entry.je_status,
      checklistComplete: entry.checklist_complete,
      checkedCount: entry.checked_count,
    }),
  }))
}

export function patchIncomeAllocationMonthsSummary(
  months: WaterfallJeMonthsSummaryMonth[],
  reportMonth: string,
  patch: {
    checked_count: number
    checklist_complete: boolean
    can_post_je?: boolean | null
  },
): WaterfallJeMonthsSummaryMonth[] {
  const existing = months.find((entry) => entry.report_month === reportMonth)
  if (!existing) {
    return [
      ...months,
      {
        report_month: reportMonth,
        je_status: 'none',
        checklist_complete: patch.checklist_complete,
        can_post_je: patch.can_post_je ?? patch.checklist_complete,
        checked_count: patch.checked_count,
      },
    ]
  }

  const nextCanPostJe = patch.can_post_je ?? existing.can_post_je
  if (
    existing.checked_count === patch.checked_count &&
    existing.checklist_complete === patch.checklist_complete &&
    existing.can_post_je === nextCanPostJe
  ) {
    return months
  }

  return months.map((entry) =>
    entry.report_month === reportMonth
      ? {
          ...entry,
          checked_count: patch.checked_count,
          checklist_complete: patch.checklist_complete,
          can_post_je: nextCanPostJe,
        }
      : entry,
  )
}

/** Union API months with the local 2025-01 … last-full-month range (newest first). */
export function mergeIncomeAllocationMonthSelectOptions(
  apiMonths: WaterfallJeMonthsSummaryMonth[],
  asOf: Date = new Date(),
): SelectOption[] {
  const byMonth = new Map(apiMonths.map((entry) => [entry.report_month, entry]))

  return incomeAllocationMonthsRange(asOf).map((reportMonth) => {
    const entry = byMonth.get(reportMonth)
    return {
      id: reportMonth,
      label: reportMonthLabel(reportMonth),
      status: waterfallMonthSelectStatus({
        jeStatus: entry?.je_status ?? 'none',
        checklistComplete: entry?.checklist_complete ?? false,
        checkedCount: entry?.checked_count ?? 0,
        incomeAllocation: true,
      }),
    }
  })
}

export function distributionJeRowButtonLabel(
  status: WaterfallJeStatusValue | undefined,
): string {
  switch (status ?? 'none') {
    case 'none':
      return 'Create JE'
    case 'draft':
      return 'Draft'
    case 'stale':
      return 'Review'
    case 'posted':
      return 'Posted'
    default:
      return 'JE'
  }
}

export type DistributionJeRowButtonTone = 'outline' | 'amber' | 'emerald'

export function distributionJeRowButtonTone(
  status: WaterfallJeStatusValue | undefined,
): DistributionJeRowButtonTone {
  switch (status ?? 'none') {
    case 'draft':
    case 'stale':
      return 'amber'
    case 'posted':
      return 'emerald'
    default:
      return 'outline'
  }
}
